import { useEffect, useState } from 'react';
import axios from 'axios';
import type { User, ReviewerContext } from './types';

/**
 * Phase 12B — resolve which sequenced-review step (medical / legal /
 * producer) the current user acts at. Backing data:
 *   - GET /api/content-review/contexts → array of ReviewerContext,
 *     each with .reviewer._id + .domain ('medical' | 'legal' | 'compliance').
 *
 * Mapping rules (v1):
 *   - If the user's reviewer context has domain 'medical' → step 'medical'
 *   - domain 'legal' → 'legal'
 *   - domain 'compliance' → 'legal' (fold into legal for the D64 sequence
 *     — compliance is a sub-domain of legal for sequencing purposes; the
 *     historical patterns still key on compliance separately)
 *   - Otherwise, if the user has content_review.producer perm without a
 *     reviewer context → 'producer' (step 3)
 *   - Otherwise, if the user is DM/admin without a reviewer context →
 *     'producer' fallback so admins can still take the lock on any cycle
 *     (they can override step-owners via force-release regardless)
 *   - Null when the user has no meaningful step (viewer-only)
 *
 * Caching (WS1.1, walk finding — 2026-08-11): the original module-level
 * forever-cache meant a reviewer whose ReviewerContext was created AFTER
 * their first page load stayed resolved as producer/null until a hard
 * refresh. Now a short-TTL cache (60s): repeat mounts inside the window
 * reuse the fetch; anything older refetches. ReviewerContextAdmin calls
 * invalidateCurrentUserStepCache() on save so domain changes apply on
 * the next navigation without waiting out the TTL.
 */
export type SequenceStep = 'medical' | 'legal' | 'producer';

const CACHE_TTL_MS = 60_000;
let _contextsCache: { data: ReviewerContext[]; fetchedAt: number } | null = null;
// In-flight promise so N simultaneous mounts (dashboard + detail render
// together) share one request instead of racing three.
let _inflight: Promise<ReviewerContext[]> | null = null;

/** Drop the cached contexts — next mount refetches. Called by
 *  ReviewerContextAdmin after any create/update/delete. */
export function invalidateCurrentUserStepCache(): void {
  _contextsCache = null;
  _inflight = null;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

interface UseCurrentUserStepOpts {
  isProducer: boolean;
  isDecisionMaker: boolean;
  getAuthHeaders: () => Promise<Record<string, string>>;
}

export async function fetchContexts(
  getAuthHeaders: () => Promise<Record<string, string>>
): Promise<ReviewerContext[]> {
  const fresh = _contextsCache && Date.now() - _contextsCache.fetchedAt < CACHE_TTL_MS;
  if (fresh) return _contextsCache!.data;
  if (_inflight) return _inflight;

  _inflight = (async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await axios.get<ReviewerContext[]>(
        `${backendUrl}/api/content-review/contexts`,
        { headers }
      );
      const data = res.data || [];
      _contextsCache = { data, fetchedAt: Date.now() };
      return data;
    } catch {
      // A viewer-only user hits 403 here — that's expected. Don't cache
      // the failure: a later mount (maybe after a permission change)
      // should retry.
      return [];
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

export function useCurrentUserStep(
  user: User | null,
  opts: UseCurrentUserStepOpts
): SequenceStep | null {
  const [step, setStep] = useState<SequenceStep | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      if (!user) { setStep(null); return; }

      const contexts = await fetchContexts(opts.getAuthHeaders);

      if (cancelled) return;

      const match = contexts.find(
        c => c.reviewer.email?.toLowerCase() === user.email?.toLowerCase()
      );
      if (match) {
        if (match.domain === 'medical') { setStep('medical'); return; }
        if (match.domain === 'legal' || match.domain === 'compliance') {
          setStep('legal'); return;
        }
      }
      if (opts.isProducer) { setStep('producer'); return; }
      if (opts.isDecisionMaker) { setStep('producer'); return; }
      setStep(null);
    }

    resolve();
    return () => { cancelled = true; };
  }, [user, opts.isProducer, opts.isDecisionMaker, opts]);

  return step;
}
